import type { ReactNode } from 'react';
import { useState } from 'react';
import { ChevronRight, MoreVertical } from 'lucide-react';
import { Link } from 'react-router-dom';
import { StatusBadge, type StatusVariant } from './StatusBadge';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface KebabMenuItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

interface DetailHeaderProps {
  type: 'contract' | 'warranty' | 'sav' | 'user';
  title: string;
  shortTitle?: string;
  status?: { label: string; variant: StatusVariant };
  metadata?: { label: string; value: ReactNode }[];
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode;
  kebabMenuItems?: KebabMenuItem[];
  /** Custom gradient colors (overrides type-based gradient) */
  customGradient?: { from: string; to: string };
  /** Extra content rendered inside the header card (e.g. warranty price, code) */
  headerContent?: ReactNode;
}

const TYPE_GRADIENTS: Record<string, string> = {
  contract: 'tw-from-[#8074af] tw-to-[#575fa7]',
  sav: 'tw-from-[#8074af] tw-to-[#575fa7]',
  user: 'tw-from-[#8074af] tw-to-[#575fa7]',
  warranty: 'tw-from-[#c4883d] tw-to-[#a06b2e]',
};

export function DetailHeader({
  type,
  title,
  shortTitle,
  status,
  metadata = [],
  breadcrumb = [],
  actions,
  kebabMenuItems = [],
  customGradient,
  headerContent,
}: DetailHeaderProps) {
  const [showKebab, setShowKebab] = useState(false);

  return (
    <div className="tw-space-y-3">
      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <nav className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-gray-500">
          {breadcrumb.map((item, i) => (
            <span key={i} className="tw-flex tw-items-center tw-gap-1.5">
              {i > 0 && <ChevronRight className="tw-w-3.5 tw-h-3.5 tw-text-gray-400" />}
              {item.onClick ? (
                <button
                  type="button"
                  onClick={item.onClick}
                  className="hover:tw-text-gray-700 tw-transition-colors tw-text-gray-500 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-sm"
                >
                  {item.label}
                </button>
              ) : item.href ? (
                <Link
                  to={item.href}
                  className="hover:tw-text-gray-700 tw-transition-colors tw-no-underline tw-text-gray-500"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="tw-text-gray-700 tw-font-medium">{item.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}

      {/* Header card */}
      <div
        className={`tw-rounded-xl tw-px-3 sm:tw-px-6 tw-py-3 sm:tw-py-5 tw-shadow-lg tw-text-white ${
          customGradient ? '' : `tw-bg-gradient-to-br ${TYPE_GRADIENTS[type]}`
        }`}
        style={customGradient ? { background: `linear-gradient(135deg, ${customGradient.from} 0%, ${customGradient.to} 100%)` } : undefined}
      >
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-start md:tw-items-center tw-gap-2 md:tw-gap-3">
          {/* Left: title + status */}
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap tw-min-w-0 tw-flex-shrink-0">
            <h1
              className="tw-text-lg sm:tw-text-xl tw-font-bold tw-m-0 tw-truncate"
              title={title}
            >
              {shortTitle || title}
            </h1>
            {status && (
              <StatusBadge label={status.label} variant={status.variant} size="md" />
            )}
          </div>

          {/* Center: metadata or headerContent */}
          {metadata.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2 md:tw-border-l md:tw-border-white/20 md:tw-pl-5 tw-min-w-0 tw-break-words">
              {metadata.map((item, i) => (
                <div key={i} className="tw-flex tw-flex-col">
                  <span className="tw-text-[10px] tw-font-medium tw-text-white/60 tw-uppercase tw-tracking-wider">
                    {item.label}
                  </span>
                  <span className="tw-text-sm tw-font-medium tw-text-white">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          )}
          {headerContent}

          {/* Right: actions + kebab */}
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0 md:tw-ml-auto">
            {actions}
            {kebabMenuItems.length > 0 && (
              <div className="tw-relative">
                <button
                  onClick={() => setShowKebab(!showKebab)}
                  className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-white/20 tw-border tw-border-white/30 hover:tw-bg-white/30 tw-transition-colors"
                >
                  <span className="tw-hidden md:tw-inline tw-text-sm tw-font-medium">Actions</span>
                  <MoreVertical className="tw-w-5 tw-h-5" />
                </button>
                {showKebab && (
                  <>
                    <div className="tw-fixed tw-inset-0 tw-z-10" onClick={() => setShowKebab(false)} />
                    <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-z-20 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-py-1 tw-min-w-[180px]">
                      {kebabMenuItems.map((item, i) => (
                        <button
                          key={i}
                          onClick={() => { item.onClick(); setShowKebab(false); }}
                          className={`tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm tw-flex tw-items-center tw-gap-2 tw-transition-colors ${
                            item.variant === 'danger'
                              ? 'tw-text-red-600 hover:tw-bg-red-50'
                              : 'tw-text-gray-700 hover:tw-bg-gray-50'
                          }`}
                        >
                          {item.icon}
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
